/**
 * Delivery ταμείου — «Ξανά τα ίδια» και ασφάλεια ολοκλήρωσης (απόφαση ιδιοκτήτη 12/10/2026).
 *
 * Παραγωγή (κατ. 4, 90 ημέρες): 1.916 παραγγελίες delivery ταμείου, 90% από πελάτες που
 * ξαναπαραγγέλνουν, 99,8% στη ΔΙΕΥΘΥΝΣΗ ΤΗΣ ΠΡΟΗΓΟΥΜΕΝΗΣ τους παραγγελίας, 20% ακριβώς τα
 * ίδια είδη. Γι' αυτό η νέα εμφάνιση προεπιλέγει τη διεύθυνση της τελευταίας παραγγελίας
 * και δίνει «Ξανά τα ίδια» με ένα πάτημα. Καθαρές συναρτήσεις, για να δοκιμάζονται.
 */

export type DeliveryMethod = 'self' | 'efood' | 'wolt';

export interface RepeatAddress {
  id: number;
  is_default?: boolean;
  last_used_at?: string | null;
}

export interface RepeatOrderRef {
  id: number;
  order_type?: string | null;
  status?: string | null;
  delivery_address_id?: number | string | null;
}

// Ακυρωμένη/απορριφθείσα παραγγελία δεν είναι «η τελευταία»: ίσως ακυρώθηκε ακριβώς επειδή
// ήταν λάθος (είδη ή διεύθυνση).
const CLOSED_STATUSES = new Set(['cancelled', 'canceled', 'rejected']);

const isOpenDeliveryOrder = (o: RepeatOrderRef): boolean =>
  o.order_type === 'delivery' && !CLOSED_STATUSES.has(String(o.status || '').toLowerCase());

/** Η τελευταία (μη ακυρωμένη) παραγγελία delivery — οι λίστες έρχονται νεότερη πρώτη. */
export function lastDeliveryOrder<T extends RepeatOrderRef>(orders?: T[] | null): T | null {
  return (orders || []).find(isOpenDeliveryOrder) ?? null;
}

/**
 * Η διεύθυνση που προεπιλέγεται όταν ανοίγει ο πελάτης (νέα εμφάνιση):
 * της τελευταίας παραγγελίας delivery (αν η διεύθυνση υπάρχει ακόμη) → η πιο πρόσφατη
 * last_used_at → η προεπιλεγμένη → η μοναδική. Αλλιώς καμία: διαλέγει ο ταμίας.
 */
export function preferredAddress<A extends RepeatAddress>(
  addresses?: A[] | null,
  orders?: RepeatOrderRef[] | null,
): A | null {
  const list = addresses || [];
  if (list.length === 0) return null;
  for (const o of orders || []) {
    if (!isOpenDeliveryOrder(o) || o.delivery_address_id == null) continue;
    const hit = list.find(a => Number(a.id) === Number(o.delivery_address_id));
    if (hit) return hit;
  }
  const used = list.filter(a => a.last_used_at && !Number.isNaN(Date.parse(a.last_used_at)));
  if (used.length > 0) {
    return used.reduce((best, a) => (Date.parse(a.last_used_at!) > Date.parse(best.last_used_at!) ? a : best));
  }
  const def = list.find(a => a.is_default);
  if (def) return def;
  return list.length === 1 ? list[0] : null;
}

/** Οι τρόποι παράδοσης που έχει ανοίξει το κατάστημα, με τη σειρά των κουμπιών. */
export function deliveryMethodOptions(store?: {
  delivery_self?: boolean | null; delivery_efood?: boolean | null; delivery_wolt?: boolean | null;
} | null): DeliveryMethod[] {
  const options: DeliveryMethod[] = [];
  if (store?.delivery_self) options.push('self');
  if (store?.delivery_efood) options.push('efood');
  if (store?.delivery_wolt) options.push('wolt');
  return options;
}

/**
 * Ο τρόπος παράδοσης που εννοείται χωρίς επιλογή — ΜΟΝΟ ο δικός μας διανομέας: όταν είναι ο
 * μόνος ανοιχτός, ή όταν το κατάστημα δεν έχει ρυθμίσει κανέναν (όπως ως τώρα: 'self').
 * Όπου υπάρχει e-food/Wolt (κλείνει κούριερ με χρέωση) ο ταμίας διαλέγει ρητά σε ΚΑΘΕ
 * παραγγελία — null = «δεν εννοείται».
 */
export function impliedDeliveryMethod(options: DeliveryMethod[]): DeliveryMethod | null {
  if (options.length === 0) return 'self';
  if (options.length === 1 && options[0] === 'self') return 'self';
  return null;
}

export interface SummaryItem {
  product_name?: string | null;
  quantity?: number | string | null;
}

/** «2× Φρέντο · 1× Τοστ» + πόσα ακόμη — για την αναγγελία κλήσης. */
export function summarizeOrderItems(items: SummaryItem[] | null | undefined, max = 3): { text: string; more: number } {
  const list = (items || []).filter(i => i && i.product_name);
  const shown = list.slice(0, max).map(i => {
    const q = Number(i.quantity);
    const qty = Number.isFinite(q) && q > 0
      ? (Number.isInteger(q) ? String(q) : q.toLocaleString('el-GR', { maximumFractionDigits: 3 }))
      : '1';
    return `${qty}× ${i.product_name}`;
  });
  return { text: shown.join(' · '), more: Math.max(0, list.length - shown.length) };
}
